import { z } from "zod";
import {
	AdminListInputSchema,
	adminItemOutput,
	adminListOutput,
} from "./common";

// =============
// Admin Partner Earning Schemas
// =============

/**
 * Partner earning status enum.
 */
export const PartnerEarningStatusSchema = z.enum([
	"accrued",
	"processing",
	"paid",
	"void",
]);
export type PartnerEarningStatus = z.infer<typeof PartnerEarningStatusSchema>;

/**
 * Admin partner earning schema.
 */
export const AdminPartnerEarningSchema = z.object({
	id: z.string(),
	appId: z.string(),
	partnerId: z.string(),
	installId: z.string(),
	payoutId: z.string().nullable(),
	transactionId: z.string(),
	amountCentsUSD: z.number().int(),
	periodStart: z.coerce.date().nullable(),
	periodEnd: z.coerce.date().nullable(),
	createdAt: z.coerce.date(),
	status: PartnerEarningStatusSchema,
});
export type AdminPartnerEarning = z.infer<typeof AdminPartnerEarningSchema>;

/**
 * Admin partner earning list input with filters.
 */
export const AdminPartnerEarningListInputSchema = AdminListInputSchema.extend({
	filter: z
		.object({
			appId: z.string().optional(),
			partnerId: z.string().optional(),
			installId: z.string().optional(),
			payoutId: z.string().optional(),
			status: PartnerEarningStatusSchema.optional(),
			createdAfter: z.coerce.date().optional(),
			createdBefore: z.coerce.date().optional(),
		})
		.optional(),
});
export type AdminPartnerEarningListInput = z.infer<
	typeof AdminPartnerEarningListInputSchema
>;

/**
 * Admin partner earning list output.
 */
export const AdminPartnerEarningListOutputSchema = adminListOutput(
	AdminPartnerEarningSchema,
);
export type AdminPartnerEarningListOutput = z.infer<
	typeof AdminPartnerEarningListOutputSchema
>;

/**
 * Admin partner earning single item output.
 */
export const AdminPartnerEarningOutputSchema = adminItemOutput(
	AdminPartnerEarningSchema,
);
export type AdminPartnerEarningOutput = z.infer<
	typeof AdminPartnerEarningOutputSchema
>;

/**
 * Admin partner earning create input.
 */
export const AdminPartnerEarningCreateInputSchema = z.object({
	appId: z.string(),
	partnerId: z.string(),
	installId: z.string(),
	transactionId: z.string(),
	amountCentsUSD: z.number().int(),
	periodStart: z.coerce.date().optional(),
	periodEnd: z.coerce.date().optional(),
	status: PartnerEarningStatusSchema,
});
export type AdminPartnerEarningCreateInput = z.infer<
	typeof AdminPartnerEarningCreateInputSchema
>;

/**
 * Admin partner earning update input.
 */
export const AdminPartnerEarningUpdateInputSchema = z.object({
	id: z.string(),
	status: PartnerEarningStatusSchema.optional(),
	payoutId: z.string().nullable().optional(),
});
export type AdminPartnerEarningUpdateInput = z.infer<
	typeof AdminPartnerEarningUpdateInputSchema
>;
